import React, { useCallback, useState, useEffect } from 'react';
import {
    Button, Modal, Box, Select, Typography, InputLabel,
    MenuItem, IconButton, List, ListItem, ListItemButton,
    ListItemIcon, ListItemText, Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useInitialData, useSelection } from '../services/localStorageContext';
import AddBrokerModal from './AddBrokerModal';

const boxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 4,
};

const closeButtonStyle = {
    position: 'absolute',
    right: 0,
};

const SelectSensorModal = React.memo(() => {
    const { initialData } = useInitialData();
    const { selection, updateSelection } = useSelection();

    const [open, setOpen] = useState(false);
    const [selectedBrokerName, setSelectedBrokerName] = useState('');
    const [tempSelection, setTempSelection] = useState({});

    // Beim Öffnen des Modals temporäre Auswahl übernehmen
    const handleOpen = useCallback(() => {
        setTempSelection(selection);
        setOpen(true);
    }, [selection]);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleBrokerChange = useCallback((event) => {
        setSelectedBrokerName(event?.target.value);
    }, []);

    // Sensor-Auswahl umschalten, aber nur lokal in tempSelection
    const handleToggleSensor = useCallback((sensorName) => {
        setTempSelection((prevSelection) => {
            const currentSelection = { ...prevSelection };

            // Falls noch keine Auswahl existiert, leeres Objekt anlegen
            if (!currentSelection[selectedBrokerName]) {
                currentSelection[selectedBrokerName] = [];
            }

            const sensors = currentSelection[selectedBrokerName];
            const sensorIndex = sensors.indexOf(sensorName);

            if (sensorIndex !== -1) {
                // Sensor entfernen
                const newSensors = sensors.filter(s => s !== sensorName);
                if (newSensors.length === 0) {
                    // Broker komplett entfernen
                    const { [selectedBrokerName]: _, ...rest } = currentSelection;
                    return rest;
                } else {
                    return {
                        ...currentSelection,
                        [selectedBrokerName]: newSensors
                    };
                }
            } else {
                // Sensor hinzufügen
                return {
                    ...currentSelection,
                    [selectedBrokerName]: [...sensors, sensorName]
                };
            }
        });
    }, [selectedBrokerName]);

    // "Update View" übernimmt die tempSelection in die globale selection
    const updateView = useCallback(() => {
        updateSelection(tempSelection);
        setOpen(false);
    }, [tempSelection, updateSelection]);

    const renderSensorsChecklist = useCallback(() => {
        if (selectedBrokerName && initialData && initialData[selectedBrokerName]) {
            const sensorsForBroker = Object.keys(initialData[selectedBrokerName]);
            return (
                <div>
                    <List>
                        {sensorsForBroker.map((sensorName) => (
                            <ListItem key={sensorName} disablePadding>
                                <ListItemButton onClick={() => handleToggleSensor(sensorName)}>
                                    <ListItemIcon>
                                        <Checkbox
                                            edge="start"
                                            checked={
                                                tempSelection.hasOwnProperty(selectedBrokerName) &&
                                                tempSelection[selectedBrokerName].includes(sensorName)
                                            }
                                        />
                                    </ListItemIcon>
                                    <ListItemText primary={sensorName} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                    <Button onClick={updateView}>Update View</Button>
                </div>
            );
        }
        return null;
    }, [selectedBrokerName, initialData, tempSelection, handleToggleSensor, updateView]);

    return (
        <div>
            <Button
                onClick={handleOpen}
                variant="contained"
                style={{ backgroundColor: '#d9e4eb', color: 'black' }}
            >
                Connect to Sensor
            </Button>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={boxStyle}>
                    <IconButton
                        sx={closeButtonStyle}
                        onClick={handleClose}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography id="modal-modal-title" variant="h6" component="h2">
                        Show Sensor
                    </Typography>
                    {initialData ? (
                        <div>
                            <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                                Please select a broker and its sensor to view its data.
                            </Typography>
                            <InputLabel>Broker</InputLabel>
                            <Select
                                value={selectedBrokerName}
                                label="Broker"
                                autoWidth
                                onChange={handleBrokerChange}
                            >
                                {Object.keys(initialData).map((brokerName) => (
                                    <MenuItem
                                        key={brokerName}
                                        value={brokerName}
                                    >
                                        {brokerName}
                                    </MenuItem>
                                ))}
                            </Select>
                            {renderSensorsChecklist()}
                        </div>
                    ) : (
                        <div>
                            <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                                Please add a broker to view its sensors and data.
                            </Typography>
                            <AddBrokerModal closeModal={handleClose} />
                        </div>
                    )}
                </Box>
            </Modal>
        </div>
    );
});

export default SelectSensorModal;